import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Flame, ShoppingBag, Plus, Minus, Trash2, X, ChevronRight, Loader2, MapPin, Store, Truck, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { fullMenu } from '../data/mock';
import axios from 'axios';
import { logger } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const menuSections = [
  { id: 'pizza', label: 'Pizza', data: fullMenu.pizza },
  { id: 'pasta', label: 'Pasta', data: fullMenu.pasta },
  { id: 'salads', label: 'Salads', data: fullMenu.salads },
  { id: 'risotto', label: 'Risotto', data: fullMenu.risotto },
  { id: 'antipasti', label: 'Antipasti', data: fullMenu.antipasti },
  { id: 'tradizionaliItaliani', label: 'Tradizionali Italiani', data: fullMenu.tradizionaliItaliani },
  { id: 'caldiFritti', label: 'Caldi & Fritti', data: fullMenu.caldiFritti },
  { id: 'meatAndFish', label: 'Meat & Fish', data: fullMenu.meatAndFish },
  { id: 'kidsMenu', label: 'Kids Menu', data: fullMenu.kidsMenu },
  { id: 'desserts', label: 'Desserts', data: fullMenu.desserts },
  { id: 'softDrinks', label: 'Soft Drinks', data: fullMenu.softDrinks },
];

function parsePrice(priceStr) {
  const matches = priceStr.match(/[\d]+\.[\d]+/);
  if (matches) return parseFloat(matches[0]);
  const intMatch = priceStr.match(/[\d]+/);
  if (intMatch) return parseFloat(intMatch[0]);
  return 0;
}

const ALLOWED_POSTCODES = ['LU5', 'LU6'];

const STANDARD_EXTRAS = [
  { name: 'Mushrooms', price: 1.5 },
  { name: 'Onions', price: 1.5 },
  { name: 'Peppers', price: 1.5 },
  { name: 'Rocket', price: 1.5 },
  { name: 'Black Olives', price: 1.5 },
  { name: 'Cherry Tomatoes', price: 1.5 },
];

const PREMIUM_EXTRAS = [
  { name: 'Chicken', price: 2.5 },
  { name: 'Prawns', price: 5.5 },
  { name: 'Burrata', price: 5.0 },
  { name: 'Extra Mozzarella', price: 3.5 },
];

const SUPPORTS_EXTRAS = ['pizza', 'pasta'];

const OrderPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pizza');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(false);
  const [orderType, setOrderType] = useState('collection'); // 'collection' or 'delivery'
  const [paymentMethod, setPaymentMethod] = useState('in_store'); // 'in_store' or 'online'
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '', email: '', phone: '', special_instructions: ''
  });
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: '', postcode: ''
  });
  const [postcodeError, setPostcodeError] = useState('');
  const [orderingOpen, setOrderingOpen] = useState(null); // null = loading, true/false
  const [orderingMessage, setOrderingMessage] = useState('');
  const [collectionAvailable, setCollectionAvailable] = useState(true);
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  const [collectionMessage, setCollectionMessage] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [collectionHours, setCollectionHours] = useState('');
  const [deliveryHours, setDeliveryHours] = useState('');
  const [extrasModal, setExtrasModal] = useState(null); // { item, category } or null
  const [selectedExtras, setSelectedExtras] = useState({}); // { 'Mushrooms': true, ... }
  const [deliveryMinimum, setDeliveryMinimum] = useState(15.0);
  const [outOfStock, setOutOfStock] = useState(new Set()); // Set of "<category>::<name>" keys

  useEffect(() => {
    axios.get(`${API}/menu/stock`).then(res => {
      const items = res.data?.items || [];
      const oos = new Set(items.filter(i => !i.in_stock).map(i => i.key));
      setOutOfStock(oos);
    }).catch(() => { /* default to all in stock */ });
  }, []);

  useEffect(() => {
    axios.get(`${API}/orders/status`).then(res => {
      setOrderingOpen(res.data.is_open);
      setOrderingMessage(res.data.message);
      if (typeof res.data.delivery_minimum === 'number') {
        setDeliveryMinimum(res.data.delivery_minimum);
      }
      if (res.data.collection) {
        setCollectionAvailable(res.data.collection.is_open);
        setCollectionMessage(res.data.collection.message || '');
        setCollectionHours(res.data.collection.hours || '');
      }
      if (res.data.delivery) {
        setDeliveryAvailable(res.data.delivery.is_open);
        setDeliveryMessage(res.data.delivery.message || '');
        setDeliveryHours(res.data.delivery.hours || '');
      }
    }).catch(() => {
      setOrderingOpen(true); // fallback to open on error
    });
  }, []);

  const addToCart = (item, category) => {
    if (!orderingOpen) {
      toast.error('Ordering is currently closed');
      return;
    }
    const price = parsePrice(item.price);
    if (price <= 0) {
      toast.info('Please contact us for pricing on this item');
      return;
    }
    // For pizza/pasta, open the extras modal first
    if (SUPPORTS_EXTRAS.includes(category)) {
      setSelectedExtras({});
      setExtrasModal({ item, category });
      return;
    }
    addItemDirectly(item, category);
  };

  const addItemDirectly = (item, category, extras = []) => {
    const price = parsePrice(item.price);
    const newId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setCart(prev => {
      // Items with extras are always added as a new line (don't merge)
      if (extras.length > 0) {
        return [...prev, { uid: newId, name: item.name, category, price, quantity: 1, extras }];
      }
      const existing = prev.find(c => c.name === item.name && c.category === category && (!c.extras || c.extras.length === 0));
      if (existing) {
        return prev.map(c =>
          c.name === item.name && c.category === category && (!c.extras || c.extras.length === 0)
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, { uid: newId, name: item.name, category, price, quantity: 1, extras: [] }];
    });
    toast.success(`${item.name} added to order`);
  };

  const confirmExtras = () => {
    if (!extrasModal) return;
    const { item, category } = extrasModal;
    const allExtras = [...STANDARD_EXTRAS, ...PREMIUM_EXTRAS];
    const chosen = allExtras.filter(e => selectedExtras[e.name]);
    addItemDirectly(item, category, chosen);
    setExtrasModal(null);
    setSelectedExtras({});
  };

  const toggleExtra = (extraName) => {
    setSelectedExtras(prev => ({ ...prev, [extraName]: !prev[extraName] }));
  };

  const updateQuantity = (index, delta) => {
    setCart(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: updated[index].quantity + delta };
      if (updated[index].quantity <= 0) return prev.filter((_, i) => i !== index);
      return updated;
    });
  };

  const removeItem = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const lineTotal = (item) => {
    const extrasTotal = (item.extras || []).reduce((s, e) => s + e.price, 0);
    return (item.price + extrasTotal) * item.quantity;
  };

  const cartTotal = cart.reduce((sum, item) => sum + lineTotal(item), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // When switching to delivery, force payment to online
  const handleOrderTypeChange = (type) => {
    setOrderType(type);
    if (type === 'delivery') {
      setPaymentMethod('online');
    }
    setPostcodeError('');
  };

  const validatePostcode = (postcode) => {
    const clean = postcode.trim().toUpperCase().replace(/\s+/g, '');
    const valid = ALLOWED_POSTCODES.some(pc => clean.startsWith(pc));
    if (!valid && postcode.length > 0) {
      setPostcodeError(`Sorry, we only deliver to ${ALLOWED_POSTCODES.join(' & ')} postcodes`);
    } else {
      setPostcodeError('');
    }
    return valid;
  };

  const handleSubmitOrder = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (orderType === 'delivery') {
      if (!deliveryInfo.address || !deliveryInfo.postcode) {
        toast.error('Please fill in your delivery address and postcode');
        return;
      }
      if (!validatePostcode(deliveryInfo.postcode)) {
        return;
      }
      if (cartTotal < deliveryMinimum) {
        toast.error(`Minimum delivery order is £${deliveryMinimum.toFixed(2)}. Add £${(deliveryMinimum - cartTotal).toFixed(2)} more to continue.`);
        return;
      }
    }

    setLoading(true);
    try {
      // Flatten cart: each item plus its extras as separate line items
      const flatItems = [];
      cart.forEach(item => {
        flatItems.push({
          name: item.name,
          category: item.category,
          price: item.price,
          quantity: item.quantity,
        });
        (item.extras || []).forEach(extra => {
          flatItems.push({
            name: `Extra: ${extra.name} (${item.name})`,
            category: 'extras',
            price: extra.price,
            quantity: item.quantity,
          });
        });
      });

      const orderPayload = {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        order_type: orderType,
        payment_method: paymentMethod,
        items: flatItems,
        special_instructions: customerInfo.special_instructions || '',
        delivery_address: orderType === 'delivery' ? deliveryInfo.address : '',
        delivery_postcode: orderType === 'delivery' ? deliveryInfo.postcode : '',
      };

      const response = await axios.post(`${API}/orders`, orderPayload);
      const order = response.data;

      if (paymentMethod === 'online') {
        // Create Stripe checkout session
        const checkoutRes = await axios.post(`${API}/orders/checkout`, {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        window.location.href = checkoutRes.data.url;
      } else {
        // Collection - pay in store
        toast.success('Order placed! Please collect and pay in store.');
        navigate(`/order/success?order_id=${order.id}&type=collection`);
      }
    } catch (error) {
      logger.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const MenuItem = ({ item, section }) => {
    const price = parsePrice(item.price);
    const isAskStaff = price <= 0;
    const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
    const isOutOfStock = outOfStock.has(`${section.id}::${item.name}`);
    return (
      <div data-testid={`menu-item-${item.name.replace(/\s+/g, '-').toLowerCase()}`} className={`bg-white rounded-xl p-5 border border-[#1B4332]/10 shadow-sm group hover:shadow-md transition-all duration-200 flex flex-col justify-between ${isOutOfStock ? 'opacity-60' : ''}`}>
        <div>
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2 flex-1">
              <h3 className="font-serif text-lg font-semibold text-[#1B4332]">{item.name}</h3>
              {item.vegetarian && (
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex-shrink-0">V</span>
              )}
              {item.spicy && <Flame size={16} className="text-red-500 flex-shrink-0" />}
              {isOutOfStock && (
                <span data-testid={`sold-out-${item.name.replace(/\s+/g, '-').toLowerCase()}`} className="text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full flex-shrink-0 uppercase tracking-wide">
                  Sold Out
                </span>
              )}
            </div>
          </div>
          {item.description && (
            <p className="text-[#636E72] text-sm mb-3">{item.description}</p>
          )}
        </div>
        {hasVariants ? (
          <div className="mt-2 space-y-2">
            <span className="font-bold text-[#C67C4E] block">{item.price}</span>
            {orderingOpen && !isOutOfStock && (
              <div className="flex flex-wrap gap-2">
                {item.variants.map((v) => (
                  <Button
                    key={v.name}
                    data-testid={`add-to-cart-${item.name.replace(/\s+/g, '-').toLowerCase()}-${v.name.replace(/\s+/g, '-').toLowerCase()}`}
                    size="sm"
                    onClick={() => addToCart({ name: `${item.name} (${v.name})`, price: v.price }, section.id)}
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white rounded-full px-4 h-9 opacity-80 group-hover:opacity-100 transition-opacity"
                  >
                    <Plus size={16} className="mr-1" /> {v.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between mt-2">
            <span className="font-bold text-[#C67C4E]">{item.price}</span>
            {!isAskStaff && orderingOpen && !isOutOfStock && (
              <Button
                data-testid={`add-to-cart-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                size="sm"
                onClick={() => addToCart(item, section.id)}
                className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white rounded-full px-4 h-9 opacity-80 group-hover:opacity-100 transition-opacity"
              >
                <Plus size={16} className="mr-1" /> Add
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Checkout Form
  if (checkoutStep) {
    return (
      <div className="min-h-screen bg-[#FFFBF5]">
        <Header />
        <section className="pt-28 pb-16 px-4">
          <div className="max-w-2xl mx-auto">
            <button
              data-testid="back-to-menu-btn"
              onClick={() => setCheckoutStep(false)}
              className="flex items-center gap-1 text-[#636E72] hover:text-[#1B4332] mb-6 transition-colors"
            >
              <ChevronRight size={18} className="rotate-180" /> Back to menu
            </button>

            <h1 className="font-serif text-4xl font-bold text-[#1B4332] mb-8">Checkout</h1>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-6">
              <h2 className="font-serif text-xl font-semibold text-[#1B4332] mb-4">Your Order</h2>
              <div className="space-y-3">
                {cart.map((item, i) => (
                  <div key={item.uid || `${item.name}-${i}`} className="py-2 border-b border-gray-100 last:border-0">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#1B4332] text-white text-xs flex items-center justify-center font-medium">
                          {item.quantity}
                        </span>
                        <span className="text-[#2D3436]">{item.name}</span>
                      </div>
                      <span className="font-medium text-[#1B4332]">
                        {"\u00A3"}{lineTotal(item).toFixed(2)}
                      </span>
                    </div>
                    {item.extras && item.extras.length > 0 && (
                      <ul className="ml-10 mt-1 text-xs text-[#636E72] space-y-0.5">
                        {item.extras.map((e) => (
                          <li key={`${item.uid}-${e.name}`} className="flex justify-between">
                            <span>+ {e.name}</span>
                            <span>{"\u00A3"}{(e.price * item.quantity).toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t-2 border-[#1B4332]/20 flex justify-between">
                <span className="font-serif text-xl font-bold text-[#1B4332]">Total</span>
                <span className="font-serif text-xl font-bold text-[#C67C4E]">
                  {"\u00A3"}{cartTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-6">
              <h2 className="font-serif text-xl font-semibold text-[#1B4332] mb-4">Your Details</h2>
              <div className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    data-testid="checkout-name-input"
                    placeholder="Your full name"
                    value={customerInfo.name}
                    onChange={e => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      data-testid="checkout-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={customerInfo.email}
                      onChange={e => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      data-testid="checkout-phone-input"
                      type="tel"
                      placeholder="+44 7123 456789"
                      value={customerInfo.phone}
                      onChange={e => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Special Instructions</Label>
                  <Input
                    data-testid="checkout-instructions-input"
                    placeholder="Any allergies or special requests?"
                    value={customerInfo.special_instructions}
                    onChange={e => setCustomerInfo({ ...customerInfo, special_instructions: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Order Type: Collection or Delivery */}
            <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-6">
              <h2 className="font-serif text-xl font-semibold text-[#1B4332] mb-4">Collection or Delivery?</h2>
              {(!collectionAvailable || !deliveryAvailable) && (
                <div data-testid="ordering-paused-banner" className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm">
                  {!collectionAvailable && (
                    <p className="text-amber-900"><strong>Collection:</strong> {collectionMessage}</p>
                  )}
                  {!deliveryAvailable && (
                    <p className="text-amber-900 mt-1"><strong>Delivery:</strong> {deliveryMessage}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  data-testid="order-type-collection"
                  onClick={() => collectionAvailable && handleOrderTypeChange('collection')}
                  disabled={!collectionAvailable}
                  className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                    !collectionAvailable
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : orderType === 'collection'
                      ? 'border-[#1B4332] bg-[#1B4332]/5'
                      : 'border-gray-200 hover:border-[#C67C4E]/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Store size={24} className={orderType === 'collection' ? 'text-[#1B4332]' : 'text-gray-400'} />
                    <p className="font-semibold text-[#1B4332] text-lg">Collection</p>
                    {!collectionAvailable && <span className="ml-auto text-xs text-red-600 font-medium">Unavailable</span>}
                  </div>
                  <p className="text-sm text-[#636E72]">Pick up from our restaurant</p>
                </button>
                <button
                  data-testid="order-type-delivery"
                  onClick={() => deliveryAvailable && handleOrderTypeChange('delivery')}
                  disabled={!deliveryAvailable}
                  className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                    !deliveryAvailable
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : orderType === 'delivery'
                      ? 'border-[#1B4332] bg-[#1B4332]/5'
                      : 'border-gray-200 hover:border-[#C67C4E]/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Truck size={24} className={orderType === 'delivery' ? 'text-[#1B4332]' : 'text-gray-400'} />
                    <p className="font-semibold text-[#1B4332] text-lg">Delivery</p>
                    {!deliveryAvailable && <span className="ml-auto text-xs text-red-600 font-medium">Unavailable</span>}
                  </div>
                  <p className="text-sm text-[#636E72]">We deliver to LU5 & LU6 only</p>
                </button>
              </div>
            </div>

            {/* Delivery Address (only if delivery) */}
            {orderType === 'delivery' && (
              <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-6">
                <h2 className="font-serif text-xl font-semibold text-[#1B4332] mb-4">
                  <MapPin size={20} className="inline mr-2 text-[#C67C4E]" />
                  Delivery Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Address *</Label>
                    <Input
                      data-testid="delivery-address-input"
                      placeholder="House number, street name, town"
                      value={deliveryInfo.address}
                      onChange={e => setDeliveryInfo({ ...deliveryInfo, address: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Postcode *</Label>
                    <Input
                      data-testid="delivery-postcode-input"
                      placeholder="e.g. LU5 4AB"
                      value={deliveryInfo.postcode}
                      onChange={e => {
                        setDeliveryInfo({ ...deliveryInfo, postcode: e.target.value });
                        if (e.target.value.length >= 3) validatePostcode(e.target.value);
                        else setPostcodeError('');
                      }}
                      className={`mt-1 ${postcodeError ? 'border-red-500' : ''}`}
                    />
                    {postcodeError && (
                      <p data-testid="postcode-error" className="text-red-500 text-sm mt-1">{postcodeError}</p>
                    )}
                    <p className="text-xs text-[#636E72] mt-1">We deliver to LU5 and LU6 postcodes only</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-8">
              <h2 className="font-serif text-xl font-semibold text-[#1B4332] mb-4">Payment</h2>
              {orderType === 'delivery' ? (
                <div className="p-5 rounded-xl border-2 border-[#1B4332] bg-[#1B4332]/5">
                  <p className="font-semibold text-[#1B4332] text-lg">Pay Online</p>
                  <p className="text-sm text-[#636E72] mt-1">Secure card payment via Stripe (required for delivery)</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    data-testid="payment-method-in-store"
                    onClick={() => setPaymentMethod('in_store')}
                    className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                      paymentMethod === 'in_store'
                        ? 'border-[#1B4332] bg-[#1B4332]/5'
                        : 'border-gray-200 hover:border-[#C67C4E]/50'
                    }`}
                  >
                    <p className="font-semibold text-[#1B4332] text-lg">Pay in Store</p>
                    <p className="text-sm text-[#636E72] mt-1">Pay when you collect your order</p>
                  </button>
                  <button
                    data-testid="payment-method-online"
                    onClick={() => setPaymentMethod('online')}
                    className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                      paymentMethod === 'online'
                        ? 'border-[#1B4332] bg-[#1B4332]/5'
                        : 'border-gray-200 hover:border-[#C67C4E]/50'
                    }`}
                  >
                    <p className="font-semibold text-[#1B4332] text-lg">Pay Online</p>
                    <p className="text-sm text-[#636E72] mt-1">Secure card payment via Stripe</p>
                  </button>
                </div>
              )}
            </div>

            {/* Delivery minimum warning */}
            {orderType === 'delivery' && cartTotal < deliveryMinimum && (
              <div data-testid="delivery-minimum-warning" className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                <Truck size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">
                    Minimum delivery order is £{deliveryMinimum.toFixed(2)}
                  </p>
                  <p className="text-amber-800 mt-0.5">
                    Add £{(deliveryMinimum - cartTotal).toFixed(2)} more to qualify, or switch to Collection.
                  </p>
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              data-testid="place-order-btn"
              onClick={handleSubmitOrder}
              disabled={loading || (orderType === 'delivery' && !!postcodeError) || (orderType === 'delivery' && cartTotal < deliveryMinimum)}
              className="w-full bg-[#1B4332] hover:bg-[#2D6A4F] text-white h-14 text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><Loader2 className="animate-spin mr-2" size={20} /> Processing...</>
              ) : paymentMethod === 'online' ? (
                `Pay ${"\u00A3"}${cartTotal.toFixed(2)} Online`
              ) : (
                `Place Order - ${"\u00A3"}${cartTotal.toFixed(2)}`
              )}
            </Button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block text-sm font-semibold tracking-wider text-[#C67C4E] uppercase mb-4">
            Order Online
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B4332] mb-4">
            Collection & Delivery
          </h1>
          <p className="text-base sm:text-lg text-[#636E72] max-w-2xl mx-auto">
            Browse our menu, add items to your order. Choose collection or delivery (LU5 & LU6 only).
          </p>
          <p className="text-sm text-[#C67C4E] mt-2 font-medium">
            Minimum delivery order £{deliveryMinimum.toFixed(2)}
          </p>
        </div>
      </section>

      {/* Ordering Closed Banner */}
      {orderingOpen === false && (
        <section className="px-4 pb-8">
          <div data-testid="ordering-closed-banner" className="max-w-2xl mx-auto bg-[#1B4332] text-white rounded-2xl p-8 text-center shadow-lg">
            <Clock size={36} className="mx-auto mb-3 text-[#C67C4E]" />
            <h2 className="font-serif text-2xl font-bold mb-2">Ordering is Currently Closed</h2>
            <p className="text-white/80 mb-4">Our ordering hours are:</p>
            <div className="inline-block bg-white/10 rounded-xl px-6 py-4 text-left">
              <p className="text-white font-medium" data-testid="banner-collection-hours">Collection: {collectionHours || '12pm - 9pm every day'}</p>
              <p className="text-white font-medium" data-testid="banner-delivery-hours">Delivery: {deliveryHours || 'Mon-Sat 5pm - 9pm, Sun 5pm - 8pm'}</p>
            </div>
            <p className="text-white/60 text-sm mt-4">You can still browse our menu below</p>
          </div>
        </section>
      )}

      {/* Menu */}
      <section className="pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent h-auto p-0">
              {menuSections.map(section => (
                <TabsTrigger
                  key={section.id}
                  value={section.id}
                  className="px-5 py-2.5 rounded-full border-2 border-[#1B4332] text-[#1B4332] font-medium text-sm data-[state=active]:bg-[#1B4332] data-[state=active]:text-white transition-all duration-200 hover:bg-[#1B4332]/10"
                >
                  {section.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {menuSections.map(section => (
              <TabsContent key={section.id} value={section.id} className="mt-0">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {section.data?.map((item, i) => (
                    <MenuItem key={i} item={item} section={section} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Floating Cart Button */}
      {cartCount > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            data-testid="open-cart-btn"
            onClick={() => setCartOpen(true)}
            className="bg-[#1B4332] text-white rounded-full px-6 py-4 shadow-2xl flex items-center gap-3 hover:bg-[#2D6A4F] transition-all duration-200 hover:scale-105"
          >
            <ShoppingBag size={22} />
            <span className="font-semibold">{cartCount} item{cartCount > 1 ? 's' : ''}</span>
            <span className="font-bold">{"\u00A3"}{cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCartOpen(false)} />
          <div data-testid="cart-drawer" className="relative w-full max-w-md bg-[#FFFBF5] h-full shadow-2xl flex flex-col animate-slideIn">
            <div className="flex items-center justify-between p-5 border-b border-[#1B4332]/10">
              <h2 className="font-serif text-2xl font-bold text-[#1B4332]">Your Order</h2>
              <button
                data-testid="close-cart-btn"
                onClick={() => setCartOpen(false)}
                className="p-2 hover:bg-[#1B4332]/10 rounded-full transition-colors"
              >
                <X size={22} className="text-[#1B4332]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-[#636E72]">Your order is empty</p>
                </div>
              ) : (
                cart.map((item, i) => (
                  <div key={item.uid || `${item.name}-${i}`} data-testid={`cart-item-${i}`} className="bg-white rounded-xl p-4 border border-[#1B4332]/10">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-[#1B4332]">{item.name}</p>
                        <p className="text-xs text-[#636E72] capitalize">{item.category}</p>
                        {item.extras && item.extras.length > 0 && (
                          <ul className="mt-1 text-xs text-[#636E72] space-y-0.5">
                            {item.extras.map((e) => (
                              <li key={`${item.uid}-${e.name}`}>+ {e.name} (£{e.price.toFixed(2)})</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <button
                        data-testid={`remove-cart-item-${i}`}
                        onClick={() => removeItem(i)}
                        className="text-red-400 hover:text-red-600 p-1 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`decrease-qty-${i}`}
                          onClick={() => updateQuantity(i, -1)}
                          className="w-8 h-8 rounded-full border border-[#1B4332]/20 flex items-center justify-center hover:bg-[#1B4332]/10 transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="font-medium w-6 text-center">{item.quantity}</span>
                        <button
                          data-testid={`increase-qty-${i}`}
                          onClick={() => updateQuantity(i, 1)}
                          className="w-8 h-8 rounded-full border border-[#1B4332]/20 flex items-center justify-center hover:bg-[#1B4332]/10 transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-bold text-[#C67C4E]">
                        {"\u00A3"}{lineTotal(item).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-5 border-t border-[#1B4332]/10 bg-white">
                <div className="flex justify-between mb-4">
                  <span className="font-serif text-lg font-bold text-[#1B4332]">Total</span>
                  <span className="font-serif text-lg font-bold text-[#C67C4E]">
                    {"\u00A3"}{cartTotal.toFixed(2)}
                  </span>
                </div>
                <Button
                  data-testid="proceed-to-checkout-btn"
                  onClick={() => { setCartOpen(false); setCheckoutStep(true); }}
                  className="w-full bg-[#1B4332] hover:bg-[#2D6A4F] text-white h-12 text-base rounded-xl"
                >
                  Proceed to Checkout <ChevronRight size={18} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />

      {/* Extras Modal */}
      {extrasModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="extras-modal">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl font-bold text-[#1B4332]">{extrasModal.item.name}</h3>
                <p className="text-xs text-[#636E72] mt-0.5">Optional extras (add as many as you like)</p>
              </div>
              <button
                onClick={() => { setExtrasModal(null); setSelectedExtras({}); }}
                className="p-1 text-gray-400 hover:text-gray-700"
                data-testid="extras-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#1B4332] mb-2">Standard Toppings (+£1.50 each)</p>
                <div className="grid grid-cols-2 gap-2">
                  {STANDARD_EXTRAS.map(e => (
                    <label
                      key={e.name}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${selectedExtras[e.name] ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'border-gray-200 hover:border-[#1B4332]/40'}`}
                      data-testid={`extra-${e.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedExtras[e.name]}
                        onChange={() => toggleExtra(e.name)}
                        className="hidden"
                      />
                      <span className="flex-1">{e.name}</span>
                      <span className="text-xs opacity-75">+£{e.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#C67C4E] mb-2">Premium Toppings</p>
                <div className="grid grid-cols-2 gap-2">
                  {PREMIUM_EXTRAS.map(e => (
                    <label
                      key={e.name}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${selectedExtras[e.name] ? 'bg-[#C67C4E] text-white border-[#C67C4E]' : 'border-gray-200 hover:border-[#C67C4E]/40'}`}
                      data-testid={`extra-${e.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedExtras[e.name]}
                        onChange={() => toggleExtra(e.name)}
                        className="hidden"
                      />
                      <span className="flex-1">{e.name}</span>
                      <span className="text-xs opacity-75">+£{e.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setExtrasModal(null); setSelectedExtras({}); }}
                className="flex-1"
                data-testid="extras-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmExtras}
                className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F] text-white"
                data-testid="extras-confirm-btn"
              >
                Add to Order
                {(() => {
                  const all = [...STANDARD_EXTRAS, ...PREMIUM_EXTRAS];
                  const total = parsePrice(extrasModal.item.price) + all.filter(e => selectedExtras[e.name]).reduce((s, e) => s + e.price, 0);
                  return ` · £${total.toFixed(2)}`;
                })()}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
